import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Button, Form, Modal } from 'react-bootstrap';
import SafeReactPaginate from '../General/SafeReactPaginate';
import BigSpinner from '../General/BigSpinner';
import CuponListItem from './CuponListItem';
import { toast } from '../../utilities/appToast';
import {
    fetchCuponesPendientesAdminApi,
    fetchCuponesSellerApi,
    postActualizarEstadoAprobacionAdminApi,
    postActualizarEstadoAprobacionSellerApi,
    postRevocarAprobacionSellerApi
} from '../../api/axios';
import './Cupones.css';

const ESTADOS = {
    1: 'Pendiente',
    2: 'Aprobado',
    3: 'Rechazado',
    4: 'Expirado'
};

const VIGENCIAS = [
    { value: '', label: 'Todas las vigencias' },
    { value: '1', label: 'Vigencia 1' },
    { value: '2', label: 'Vigencia 2' },
    { value: '3', label: 'Vigencia 3' }
];

const getValue = (item, ...keys) => keys.reduce((value, key) => value ?? item?.[key], undefined);

const normalizePage = (response) => ({
    items: response?.items ?? response?.Items ?? response?.content ?? response?.elementos ?? [],
    page: Number(response?.page ?? response?.Page ?? response?.numeroPagina ?? 1) || 1,
    pageSize: Number(response?.pageSize ?? response?.PageSize ?? response?.tamanoPagina ?? 10) || 10,
    totalPages: Number(response?.totalPaginas ?? response?.TotalPaginas ?? response?.totalPages ?? 0) || 0,
    totalRecords: Number(response?.totalRegistros ?? response?.TotalRegistros ?? response?.total ?? 0) || 0,
    errors: response?.errors ?? response?.Errors ?? []
});

const getErrorMessages = (error) => {
    const data = error?.response?.data;
    const errors = data?.errors ?? data?.Errors ?? data?.errores ?? [];
    if (Array.isArray(errors) && errors.length > 0) {
        return errors.map(item => typeof item === 'string' ? item : item?.message ?? item?.Message).filter(Boolean);
    }
    if (typeof data === 'string' && data.trim()) return [data];
    return [error?.message ?? 'No se pudo completar la operación.'];
};

const normalizeCoupon = (item) => ({
    ...item,
    idCupon: getValue(item, 'idCupon', 'IdCupon'),
    codCupon: getValue(item, 'codCupon', 'CodCupon', 'codigo', 'Codigo') ?? '-',
    descripcion: getValue(item, 'descripcion', 'Descripcion') ?? '-',
    fechaDesde: getValue(item, 'fechaDesde', 'FechaDesde'),
    fechaHasta: getValue(item, 'fechaHasta', 'FechaHasta'),
    porcentajeDescuento: getValue(item, 'porcentajeDescuento', 'PorcentajeDescuento'),
    valorDescuento: getValue(item, 'valorDescuento', 'ValorDescuento'),
    topeReintegro: getValue(item, 'topeReintegro', 'TopeReintegro'),
    porcentajeAporteSeller: getValue(item, 'porcentajeAporteSeller', 'PorcentajeAporteSeller'),
    porcentajeAporteMegatone: getValue(item, 'porcentajeAporteMegatone', 'PorcentajeAporteMegatone'),
    sellers: getValue(item, 'sellers', 'Sellers') ?? [],
    estado: getValue(item, 'estadoAprobacionSeller', 'EstadoAprobacionSeller', 'estado', 'Estado'),
});

const getStatusNumber = (status) => {
    if (typeof status === 'number') return status;
    const normalized = String(status ?? '').toLowerCase();
    return Object.entries(ESTADOS).find(([, label]) => label.toLowerCase() === normalized)?.[0] ?? status;
};

function Cupones({ idSeller, userName, userType }) {
    const isAdmin = Boolean(userType?.isAdminMod);
    const isCategory = Boolean(userType?.isCategory);
    const isAdminView = isAdmin || isCategory;
    const isSellerView = Boolean(userType?.isSeller || userType?.isSellerAdmin);
    const canManageCoupons = isAdmin || isSellerView;
    const [filters, setFilters] = useState({ texto: '', vigencia: '', estado: '', idSeller: '' });
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [result, setResult] = useState({ items: [], totalPages: 0, totalRecords: 0 });
    const [loading, setLoading] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [confirmation, setConfirmation] = useState(null);

    const loadCoupons = async () => {
        setLoading(true);
        try {
            const params = {
                Vigencia: filters.vigencia || undefined,
                Texto: filters.texto || undefined,
                Page: page,
                PageSize: pageSize
            };
            const response = isAdmin
                ? await fetchCuponesPendientesAdminApi({ ...params, IdSeller: filters.idSeller || undefined })
                : isCategory
                    ? await fetchCuponesSellerApi({ ...params, idSeller: idSeller || undefined, Estado: 2 })
                : await fetchCuponesSellerApi({ ...params, idSeller: idSeller || undefined, Estado: filters.estado || undefined });
            const normalized = normalizePage(response);
            const responseErrors = normalized.errors
                .map(item => typeof item === 'string' ? item : item?.message ?? item?.Message)
                .filter(Boolean);
            responseErrors.forEach(message => toast.error(message));
            setResult({ ...normalized, items: normalized.items.map(normalizeCoupon) });
            if (normalized.pageSize !== pageSize) setPageSize(normalized.pageSize);
        } catch (error) {
            getErrorMessages(error).forEach(message => toast.error(message));
            setResult({ items: [], totalPages: 0, totalRecords: 0 });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isAdminView || isSellerView) loadCoupons();
    }, [page, pageSize, filters.vigencia, filters.estado, filters.idSeller, filters.texto, idSeller, isAdminView, isSellerView]);

    const updateFilter = (event) => {
        setFilters(current => ({ ...current, [event.target.name]: event.target.value }));
        setPage(1);
    };

    const handlePageChange = ({ selected }) => {
        setPage(selected + 1);
    };

    const askForAction = (coupon, action) => setConfirmation({ coupon, action });

    const executeAction = async () => {
        const coupon = confirmation?.coupon;
        const action = confirmation?.action;
        if (!coupon || !action) return;

        const couponId = Number(coupon.idCupon);
        setBusyId(couponId);
        try {
            if (action === 'revoke') {
                await postRevocarAprobacionSellerApi({ idCupon: couponId, idSeller, usuario: userName || '' });
            } else if (isAdminView) {
                await postActualizarEstadoAprobacionAdminApi({ idCupon: couponId, estado: action, usuario: userName || '' });
            } else {
                await postActualizarEstadoAprobacionSellerApi({ idCupon: couponId, idSeller, estado: action, usuario: userName || '' });
            }
            toast.success('Estado del cupón actualizado.');
            setConfirmation(null);
            await loadCoupons();
        } catch (error) {
            getErrorMessages(error).forEach(message => toast.error(message));
        } finally {
            setBusyId(null);
        }
    };

    if (!isAdminView && !isSellerView) {
        return <div className="cupones-page"><div className="alert alert-warning">No tenés permisos para consultar cupones.</div></div>;
    }

    const confirmationIsRevoke = confirmation?.action === 'revoke';
    const confirmationIsApprove = confirmation?.action === 2;

    return (
        <div className="container">
            <div className="row mb-3 mb-md-4 align-items-center py-3 px-2 rounded admin-page-heading" style={{ background: 'linear-gradient(to right, #f8f9fa 0%, #fff 100%)', borderLeft: '4px solid var(--rojo-megatone, #c00)' }}>
                <div className="col-12 col-md-7">
                    <h4 className="mb-0 text-themecolor color-rojo-megatone fw-bold">Cupones</h4>
                    <p className="small text-body-secondary mb-0 mt-1">{isAdminView ? 'Revisá y decidí qué cupones se habilitan para los Sellers.' : 'Revisá las propuestas y definí tu participación.'}</p>
                </div>
                <div className="col-12 col-md-5" />
            </div>

            <div className="cupones-filters">
                {isAdminView && <Form.Group controlId="coupon-seller" className="cupones-filter-field">
                    <Form.Label>Seller</Form.Label>
                    <Form.Control name="idSeller" value={filters.idSeller} onChange={updateFilter} type="number" min="1" placeholder="ID Seller" />
                </Form.Group>}
                {isSellerView && <Form.Group controlId="coupon-state" className="cupones-filter-field">
                    <Form.Label>Estado</Form.Label>
                    <Form.Select name="estado" value={filters.estado} onChange={updateFilter}>
                        <option value="">Todos los estados</option>
                        <option value="1">Pendiente</option>
                        <option value="2">Aprobado</option>
                        <option value="3">Rechazado</option>
                        <option value="4">Expirado</option>
                    </Form.Select>
                </Form.Group>}
                <Form.Group controlId="coupon-validity" className="cupones-filter-field">
                    <Form.Label>Vigencia</Form.Label>
                    <Form.Select name="vigencia" value={filters.vigencia} onChange={updateFilter}>
                        {VIGENCIAS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </Form.Select>
                </Form.Group>
                <Form.Group controlId="coupon-text" className="cupones-filter-field cupones-filter-search">
                    <Form.Label>Buscar</Form.Label>
                    <Form.Control name="texto" value={filters.texto} onChange={updateFilter} placeholder="Código o descripción" />
                </Form.Group>
                <Button className="btn btn-sm btn-grey cupones-clear" onClick={() => { setFilters({ texto: '', vigencia: '', estado: '', idSeller: '' }); setPage(1); }}>Limpiar</Button>
            </div>

            <div className="cupones-summary">{result.totalRecords} {result.totalRecords === 1 ? 'cupón encontrado' : 'cupones encontrados'}</div>
            {loading ? <BigSpinner message="Cargando cupones..." /> : result.items.length === 0 ? (
                <div className="cupones-feedback">No se encontraron cupones.</div>
            ) : (
                <div className="cupones-list-stack">
                    {result.items.map(coupon => {
                        const status = isAdminView ? 1 : Number(getStatusNumber(coupon.estado));
                        return <CuponListItem
                            key={`${coupon.idCupon}-${coupon.idSeller ?? ''}`}
                            coupon={coupon}
                            status={status}
                            isAdminView={isAdminView}
                            canManageCoupons={canManageCoupons}
                            isBusy={busyId === Number(coupon.idCupon)}
                            onAction={askForAction}
                        />;
                    })}
                </div>
            )}
            {!loading && <div className="d-flex justify-content-center">
                <SafeReactPaginate
                    previousLabel="Anterior"
                    nextLabel="Siguiente"
                    breakLabel="..."
                    breakClassName="break-me"
                    pageCount={result.totalPages}
                    marginPagesDisplayed={2}
                    pageRangeDisplayed={2}
                    onPageChange={handlePageChange}
                    containerClassName="pagination"
                    subContainerClassName="pages pagination"
                    activeClassName="active"
                    forcePage={Math.max(0, page - 1)}
                />
            </div>}

            <Modal show={Boolean(confirmation)} onHide={() => setConfirmation(null)} centered>
                <Modal.Header closeButton><Modal.Title>{confirmationIsRevoke ? 'Revocar aprobación' : confirmationIsApprove ? 'Aprobar cupón' : 'Rechazar cupón'}</Modal.Title></Modal.Header>
                <Modal.Body>{confirmationIsRevoke ? 'La participación pasará a Expirado y el cupón dejará de aplicar a tus productos. Las compras ya realizadas no se modifican.' : confirmationIsApprove ? (isAdminView ? 'Al aprobar, el cupón se enviará a los Sellers asociados.' : 'Al aprobar, el cupón aplicará a tus productos.') : (isAdminView ? 'Al rechazar, el cupón no se enviará a ningún Seller y no podrá reactivarse.' : 'Al rechazar, no participarás de este cupón y la decisión será terminal.')}</Modal.Body>
                <Modal.Footer><Button variant="secondary" onClick={() => setConfirmation(null)} disabled={Boolean(busyId)}>Cancelar</Button><Button variant={confirmationIsApprove ? 'success' : 'danger'} onClick={executeAction} disabled={Boolean(busyId)}>{busyId ? 'Procesando...' : confirmationIsRevoke ? 'Revocar' : confirmationIsApprove ? 'Aceptar' : 'Rechazar'}</Button></Modal.Footer>
            </Modal>
        </div>
    );
}

const mapStateToProps = state => ({
    idSeller: state.general.idSeller,
    userName: state.general.userName,
    userType: state.general.userType
});

export default connect(mapStateToProps)(Cupones);
